import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import QRCode from "qrcode";
import useAuthStatus from "../../hooks/useAuthStatus";
import MobileBottomNav from "../../components/feature/MobileBottomNav";
import { useAssets } from "../../hooks/useAssets";
import { useWallet } from "../../hooks/useWallet";
import { useServices } from "../../hooks/useServices";
import ServiceGate from "../../components/feature/ServiceGate";

export default function DepositCrypto() {
  const { services } = useServices();
  const navigate = useNavigate();
  const isAuthenticated = useAuthStatus();

  const [selectedSymbol, setSelectedSymbol] = useState("USDT");
  const [copied, setCopied] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");

  const { priceMap } = useAssets();
  const { assets, loading, error } = useWallet({ enabled: isAuthenticated, priceMap });

  // assets[] from useWallet is the raw backend array with per-network addresses
  // Flatten to a simple wallets[] shape the UI already expects
  const wallets = assets.flatMap(a =>
    (a.networks || []).map(n => ({
      currency: a.currency,
      network:  n.network,
      address:  n.address,
      balance:  n.balance,
    }))
  );

  const currentWallet = wallets.find(w => w.currency === selectedSymbol) || wallets[0];

  // QR colors are intentionally fixed, not tokenized — this is a raster
  // PNG baked by the qrcode library, not CSS, so it can't react to theme
  // changes after generation. It's rendered inside its own white card
  // below (bg-white, not bg-surface) specifically so the QR's light
  // modules keep sufficient contrast against the dark "dark" module
  // color regardless of the app's theme — same reason most apps render
  // QR codes on a fixed white plate even in dark mode.
  useEffect(() => {
    if (!currentWallet?.address) return;
    QRCode.toDataURL(currentWallet.address, {
      width: 280,
      margin: 2,
      color: { dark: "#0F4C35", light: "#FFFFFF" },
    })
      .then(setQrCodeUrl)
      .catch(err => console.error("QR Code generation failed:", err));
  }, [currentWallet?.address]);

  const handleCopy = () => {
    if (!currentWallet?.address) return;
    navigator.clipboard.writeText(currentWallet.address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-bg flex flex-col items-center justify-center px-5 text-center pb-28">
        <i className="ri-lock-2-line text-6xl text-faint mb-6" />
        <h2 className="text-2xl font-bold text-primary mb-2 font-display">Sign in Required</h2>
        <p className="text-secondary mb-8">You need to be logged in to deposit crypto</p>
        <button onClick={() => navigate("/auth")} className="px-8 py-3 bg-brand text-on-brand rounded-2xl font-semibold">
          Sign In
        </button>
        <MobileBottomNav />
      </div>
    );
  }

  return (
    <ServiceGate serviceKey="deposits" services={services} fullPage>
      <div className="min-h-screen bg-bg pb-28 font-display">
      <div className="bg-surface px-5 pt-12 pb-4 flex items-center gap-3 border-b border-border">
        <button onClick={() => navigate("/deposit")} className="w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center hover:bg-surface-hover">
          <i className="ri-arrow-left-line text-secondary text-lg" />
        </button>
        <div>
          <h1 className="text-primary font-bold text-lg">Deposit Crypto</h1>
          <p className="text-muted text-xs">Send to your wallet address</p>
        </div>
      </div>

      <div className="px-5 py-5 space-y-5">
        <p className="text-secondary text-sm">Select the asset you want to deposit</p>

        {/* Asset selector — driven by raw assets (all currencies, even zero balance) */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {assets.map((asset) => (
            <button
              key={asset.currency}
              onClick={() => setSelectedSymbol(asset.currency)}
              className={`flex-shrink-0 px-4 py-2 rounded-xl border transition-all whitespace-nowrap ${
                selectedSymbol === asset.currency
                  ? "bg-brand-light border-brand text-brand-text"
                  : "bg-surface border-border text-muted"
              }`}
            >
              <span className="text-xs font-bold">{asset.currency}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="bg-surface rounded-2xl p-12 flex flex-col items-center border border-border">
            <div className="w-8 h-8 border-4 border-t-brand rounded-full animate-spin mb-4" style={{ borderColor: "var(--color-brand-light)", borderTopColor: "var(--color-brand)" }} />
            <p className="text-muted text-sm">Loading wallet…</p>
          </div>
        ) : error ? (
          <div className="bg-danger-light border border-danger-border rounded-2xl p-6 text-center">
            <p className="text-danger-text text-sm">{error}</p>
            <button onClick={() => window.location.reload()} className="mt-4 text-brand text-sm font-medium">
              Retry
            </button>
          </div>
        ) : currentWallet ? (
          <div className="bg-surface border border-border rounded-2xl p-5 space-y-5 shadow-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-primary font-bold text-sm">{currentWallet.currency} Deposit Address</p>
                <p className="text-muted text-xs mt-0.5">
                  Network: <span className="font-semibold text-secondary">{currentWallet.network || "BSC (BEP20)"}</span>
                </p>
              </div>
              <div className="w-11 h-11 rounded-full bg-brand-light flex items-center justify-center">
                <span className="font-bold text-brand-text text-sm">{currentWallet.currency}</span>
              </div>
            </div>

            {/* QR card — intentionally fixed white, see effect comment above */}
            <div className="bg-white border border-black/5 rounded-2xl p-6 flex flex-col items-center justify-center">
              {qrCodeUrl ? (
                <>
                  <img src={qrCodeUrl} alt="Wallet QR Code" className="w-52 h-52 rounded-xl" />
                  <p className="text-slate-400 text-xs mt-3">Scan to get the address</p>
                </>
              ) : (
                <div className="w-52 h-52 bg-white rounded-xl flex flex-col items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "var(--color-brand-light)", borderTopColor: "var(--color-brand)" }} />
                  <p className="text-slate-400 text-xs">Generating QR…</p>
                </div>
              )}
            </div>

            <div className="bg-surface-muted border border-border rounded-xl p-4 flex items-center gap-3">
              <p className="flex-1 text-secondary text-xs break-all font-mono leading-relaxed">
                {currentWallet.address}
              </p>
              <button
                onClick={handleCopy}
                className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all flex-shrink-0 ${
                  copied ? "bg-brand" : "bg-surface-hover"
                }`}
              >
                <i
                  className={`${copied ? "ri-check-line text-on-brand" : "ri-file-copy-line text-secondary"} text-lg`}
                />
              </button>
            </div>

            {copied && (
              <p className="text-brand-text text-center text-sm font-medium">✅ Address copied!</p>
            )}
          </div>
        ) : null}

        {currentWallet && !loading && (
          <div className="bg-warning-light border border-warning-border rounded-2xl px-4 py-4 flex gap-3">
            <i className="ri-alert-line text-warning text-xl mt-0.5 flex-shrink-0" />
            <p className="text-xs text-warning-text leading-relaxed">
              Only send <strong>{currentWallet.currency}</strong> on the{" "}
              <strong>{currentWallet.network || "BSC"}</strong> network to this address.
              Sending any other asset or using the wrong network will result in{" "}
              <strong>permanent loss of funds.</strong>
            </p>
          </div>
        )}
      </div>

      <MobileBottomNav />
    </div>
    </ServiceGate>
    
  );
}